import { AppointmentProvider, Professional } from '@aldebaran/prisma';
import { ProfessionalWithPractice } from '@aldebaran/common/types';
import { ConsultationState, RootState } from '../types/graph';

/**
 * The professional whose settings drive the patient experience: the associated
 * professional when one is selected, otherwise the practice's configured default
 * professional (`professionalByDefault`). In MMG / borne practices there is
 * often no single professional selected, so the default professional is what
 * supplies the welcome message (landing subtitle) and the phone-field flag
 * (ALDO-2540). This mirrors the personalization resolution in the graph reducer.
 */
export const getEffectiveProfessional = (
  consultation: ConsultationState,
): ProfessionalWithPractice | undefined => {
  const { professional, practice } = consultation;
  return professional ?? practice?.professionalByDefault ?? undefined;
};

/** Redux selector flavor of {@link getEffectiveProfessional}. */
export const selectEffectiveProfessional = (
  state: RootState,
): ProfessionalWithPractice | undefined =>
  getEffectiveProfessional(state.consultation);

type ProfessionalParams = Pick<
  Professional,
  'title' | 'firstname' | 'lastname'
>;

/**
 * Gets the French definite article for professional based on title
 * @param title - Professional's title (Dr, Pr, Mr, Mme), or null/undefined
 * @returns The appropriate definite article ("le" for Dr/Pr, "" for Mr/Mme and others)
 *
 * Examples:
 * - "le Dr Dupont" (Dr/Pr use "le")
 * - "Mr Martin" (Mr/Mme don't use an article)
 */
export const getProfessionalArticle = (title?: string | null): string => {
  // Dr and Pr take the definite article "le"
  if (title === 'Dr' || title === 'Pr') {
    return 'le';
  }
  // Mr and Mme are abbreviations that don't take a definite article
  return '';
};

/**
 * Gets the French preposition with article for professional based on title
 * @param title - Professional's title (Dr, Pr, Mr, Mme), or null/undefined
 * @returns The appropriate preposition ("du" for Dr/Pr, "de" for Mr/Mme and others)
 *
 * Examples:
 * - "Bienvenue au Cabinet du Dr Dupont" (Dr/Pr use "du" = "de" + "le")
 * - "Bienvenue au Cabinet de Mr Martin" (Mr/Mme use "de" without article)
 */
export const getProfessionalPreposition = (title?: string | null): string => {
  // Dr and Pr take the definite article "le", so we use "du" (de + le)
  if (title === 'Dr' || title === 'Pr') {
    return 'du';
  }
  // Mr and Mme are abbreviations that don't take a definite article, so just "de"
  return 'de';
};

export const getFullName = (professional: ProfessionalParams) =>
  `${professional.title ?? ''} ${professional.firstname ?? ''} ${
    professional.lastname ?? ''
  }`.trim();

/**
 * Formal name substituted for `{{PROFESSIONAL_NAME}}`: title + last name, with
 * **no** article — "Dr Dupont", not "le Dr Dupont".
 *
 * For standalone slots, where an article would be wrong: the report header, or
 * a label reduced to the name alone. Mid-sentence labels want the article and
 * take {@link getFormalNameWithArticle} instead.
 *
 * Examples:
 * - "Dr Dupont" (Dr/Pr)
 * - "Mr Martin" (Mr/Mme)
 */
export const getFormalName = (professional: ProfessionalParams) => {
  const title = professional.title ? `${professional.title} ` : '';
  return `${title}${professional.lastname ?? ''}`;
};

/**
 * Formal name substituted for `{{PROFESSIONAL_NAME_WITH_ARTICLE}}`: the
 * definite article followed by {@link getFormalName} — "le Dr Dupont", or
 * "Mr Martin" for the titles that take no article.
 *
 * For mid-sentence slots — "… rendez-vous avec
 * {{PROFESSIONAL_NAME_WITH_ARTICLE}} ?". The article rides on the value rather
 * than being written into the label because the same slot also has to take the
 * "ce médecin" fallback, used when no professional is resolved or the practice
 * sets `disableProfessionalNameDisplayInQuestionnaire`. The fallback replaces
 * the whole token, so a "le" hardcoded in the label reads "avec le ce médecin"
 * as soon as it applies.
 *
 * Neither variable contracts: no label may put "à" or "de" in front of one
 * ("à le Dr Dupont"). Such labels are reworded knowledge-base side.
 *
 * Examples:
 * - "le Dr Dupont" (Dr/Pr)
 * - "Mr Martin" (Mr/Mme)
 */
export const getFormalNameWithArticle = (professional: ProfessionalParams) => {
  const article = getProfessionalArticle(professional.title);
  const articleSpace = article ? `${article} ` : '';
  return `${articleSpace}${getFormalName(professional)}`;
};

/**
 * Display names for online appointment services, matching the labels shown to
 * professionals in the pro app (SERVICE_IDS in
 * apps/pro/src/components/Account/Appointment/constants/appointment.constants.ts).
 * UBICENTREX and CLICRDV are not selectable in the pro UI but can be set on the
 * back end, so they get explicit labels here too.
 */
const APPOINTMENT_SERVICE_NAMES: Record<
  Exclude<AppointmentProvider, 'OTHER'>,
  string
> = {
  DOCTOLIB: 'Doctolib',
  MADEFORMED: 'MadeForMed',
  MAIIA: 'Maiia',
  UBICENTREX: 'Ubicentrex',
  CLICRDV: 'ClicRDV',
};

/** Generic fallback when the professional has no usable appointment service. */
export const DEFAULT_APPOINTMENT_SERVICE_NAME =
  'le service de prise de rendez-vous en ligne';

type AppointmentServiceParams = Pick<
  Professional,
  'appointmentProvider' | 'appointmentProviderOther'
>;

/**
 * Resolves the human-readable name of the professional's online appointment
 * service for use in questionnaire labels. Falls back to a generic phrase when
 * no service is configured or the provider is OTHER with no custom name. The
 * name map is exhaustive over the named providers (enforced at compile time);
 * the trailing `??` is only a defensive net for stale rows whose enum value
 * predates the current map.
 */
export const getAppointmentServiceName = (
  professional: AppointmentServiceParams | null | undefined,
): string => {
  const provider = professional?.appointmentProvider;
  if (!provider) {
    return DEFAULT_APPOINTMENT_SERVICE_NAME;
  }
  if (provider === 'OTHER') {
    const other = professional.appointmentProviderOther?.trim();
    return other || DEFAULT_APPOINTMENT_SERVICE_NAME;
  }
  return (
    APPOINTMENT_SERVICE_NAMES[provider] ?? DEFAULT_APPOINTMENT_SERVICE_NAME
  );
};
